import { useCallback, useState } from "react";
import { tsApi, type MyTimesheet } from "../../api/timesheets";
import { LoadState, useLoad } from "../admin/shared";
import { csvUrl } from "../../api/periods";
import { MyPay } from "./MyPay";

const sel = "h-10 rounded-sm border border-white/15 bg-surface px-3 text-sm text-white";

export default function MyTimesheets() {
  const [period, setPeriod] = useState("");
  const loader = useCallback(() => tsApi.mine(period || undefined), [period]);
  const { data, error } = useLoad<MyTimesheet>(loader);
  return (
    <section data-testid="my-timesheets-page" className="animate-rise space-y-6">
      <div>
        <p className="label-caps text-signal">My Timesheets</p>
        <p className="mt-2 text-xs text-zinc-500">Your hours by pay period. Times are venue-local. Gross wage figures are in My Pay below — never take-home pay.</p>
      </div>
      <LoadState error={error} loading={!data && !error} testId="my-timesheets-load" />
      {data && (
        <>
          <select className={sel} value={data.period?.id ?? ""} onChange={(e) => setPeriod(e.target.value)} data-testid="my-timesheets-period-select">
            {data.periods.map((p) => <option key={p.id} value={p.id}>{p.label}{p.is_current ? " (current)" : ""}</option>)}
          </select>
          <a href={csvUrl("/me/timesheets/export.csv", { period: data.period?.id })} className="ml-3 text-xs font-semibold text-signal hover:text-white" data-testid="my-timesheets-csv">Download my timesheet (CSV)</a>
          <div className="overflow-x-auto rounded-sm border border-white/10">
            <table data-testid="my-timesheets-table" className="w-full text-left text-xs text-zinc-300">
              <thead><tr className="label-caps text-zinc-500">{["Date", "Venue", "Role", "In", "Out", "Breaks", "Paid hours", "Status", "Correction"].map((h) => <th key={h} className="px-3 py-2 font-normal">{h}</th>)}</tr></thead>
              <tbody>
                {!data.rows.length && <tr><td colSpan={9} className="px-3 py-4 text-zinc-500" data-testid="my-timesheets-empty">No sessions in this period.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.id} data-testid={`my-ts-row-${r.id}`} className="border-t border-white/5">
                    <td className="px-3 py-2 text-white">{r.work_date}{r.crosses_midnight && <span className="ml-1 text-[10px] text-indigo-300">overnight</span>}</td>
                    <td className="px-3 py-2">{r.venue_name}</td><td className="px-3 py-2">{r.role ?? "—"}</td>
                    <td className="px-3 py-2">{r.payable_in ?? "—"}</td><td className="px-3 py-2">{r.payable_out ?? (r.on_break ? "On break" : "—")}</td>
                    <td className="px-3 py-2">{r.break_count}{r.unpaid_break_seconds > 0 && ` · ${Math.round(r.unpaid_break_seconds / 60)} min unpaid`}</td>
                    <td className="px-3 py-2 font-mono text-white" data-testid={`my-ts-paid-${r.id}`}>{r.paid_hm ?? "—"} <span className="text-zinc-500">{r.paid_hours_decimal ?? ""}</span></td>
                    <td className="px-3 py-2" data-testid={`my-ts-status-${r.id}`}>{r.status_label}</td>
                    <td className="px-3 py-2">{r.correction_status ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t border-white/10"><td colSpan={6} className="px-3 py-2 text-right text-zinc-400">Total paid hours</td><td className="px-3 py-2 font-mono text-white" data-testid="my-timesheets-total">{data.total_paid_hm ?? "0:00"} <span className="text-zinc-500">{data.total_paid_hours_decimal ?? ""}</span></td><td colSpan={2} /></tr></tfoot>
            </table>
          </div>
          {data.period && <MyPay periodId={data.period.id} />}
        </>
      )}
    </section>
  );
}
